/**
 * FormActions — UX1A Platform Primitive
 *
 * Standardises Save / Cancel / Submit / Delete action placement.
 * Sticky on mobile, right-aligned on desktop.
 * Handles disabled state, loading state, and dirty-state protection.
 *
 * Usage — standard save/cancel:
 *   <FormActions
 *     onCancel={() => router.back()}
 *     onSave={handleSubmit}
 *     saving={isSaving}
 *     dirty={isDirty}
 *   />
 *
 * Usage — with destructive action:
 *   <FormActions
 *     onCancel={handleClose}
 *     onSave={handleSubmit}
 *     onDelete={handleDelete}
 *     deleteLabel="Remove record"
 *     saving={isSaving}
 *   />
 *
 * Usage — dialog footer variant (no sticky, no border):
 *   <FormActions
 *     variant="dialog"
 *     onCancel={handleClose}
 *     onSave={handleSubmit}
 *     saveLabel="Create"
 *     saving={isSaving}
 *   />
 */
import React from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "../ui/button";

export function FormActions({
  // Action handlers
  onSave,
  onCancel,
  onDelete,

  // Labels
  saveLabel = "Save changes",
  cancelLabel = "Cancel",
  deleteLabel = "Delete",

  // States
  saving = false,
  deleting = false,
  dirty = true,
  disabled = false,

  // Layout variant: "page" | "dialog" | "inline"
  variant = "page",

  "data-testid": testId,
}) {
  const isPageVariant = variant === "page";

  const wrapperClass = isPageVariant
    ? "sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background/95 backdrop-blur-sm px-4 py-3 md:px-8 md:static md:border-0 md:bg-transparent md:backdrop-blur-none md:pt-6 md:pb-0"
    : "flex flex-wrap items-center justify-end gap-2 pt-2";

  return (
    <div className={wrapperClass} data-testid={testId || "form-actions"}>
      {/* Destructive action — left side on page variant */}
      {onDelete && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDelete}
          disabled={deleting || saving}
          data-testid="form-action-delete"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          {deleting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Trash2 className="h-4 w-4 mr-2" />
          )}
          {deleteLabel}
        </Button>
      )}

      {/* Cancel + Save — right side */}
      <div className="flex items-center gap-2 ms-auto">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={saving}
            data-testid="form-action-cancel"
          >
            {cancelLabel}
          </Button>
        )}
        {onSave && (
          <Button
            type="button"
            size="sm"
            onClick={onSave}
            disabled={disabled || saving || !dirty}
            data-testid="form-action-save"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {saveLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
